import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, Check, Loader2, Sparkles, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { track } from '@/lib/analytics';
import { APP_VERSION, buildDiagnostics } from '@/lib/version';
import { useBetaTester } from '@/hooks/useBetaTester';

const KEY = 'pebble.betaBannerDismissed';
const PROMPT_KEY = 'pebble.launchPromptShown';

export default function BetaBanner() {
  const loc = useLocation();
  const [email, setEmail] = useState('');
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(KEY) === '1';
  });
  const [showLaunchPrompt, setShowLaunchPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const tester = useBetaTester();

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setEmail(data.session?.user?.email ?? ''));
  }, []);

  const finallyDismiss = () => {
    localStorage.setItem(KEY, '1');
    localStorage.setItem(PROMPT_KEY, '1');
    setDismissed(true);
    setShowLaunchPrompt(false);
  };

  const onDismissClick = () => {
    track('beta_disclaimer_dismissed');
    // Show the launch prompt once, but only if we know the user is signed in,
    // hasn't already opted in, and we haven't asked before.
    const alreadyAsked = localStorage.getItem(PROMPT_KEY) === '1';
    if (!alreadyAsked && tester.userId && !tester.optedIn && !tester.loading) {
      setShowLaunchPrompt(true);
      return;
    }
    finallyDismiss();
  };

  const onOptIn = async () => {
    setBusy(true);
    const res = await tester.optIn('banner_dismiss');
    setBusy(false);
    if (res.ok) {
      track('launch_list_opted_in', { source: 'banner_dismiss' });
      setDone(true);
      setTimeout(finallyDismiss, 1200);
    } else {
      // Silently fall back to dismissing — opt-in is optional.
      finallyDismiss();
    }
  };

  if (dismissed) return null;
  if (loc.pathname === '/' || loc.pathname === '/auth' || loc.pathname === '/reset-password') {
    return null;
  }

  const subject = encodeURIComponent(`Pebble Beta Feedback (v${APP_VERSION})`);
  const diagnostics = buildDiagnostics({
    Path: loc.pathname + loc.search,
    From: email || 'beta tester',
  });
  const body = encodeURIComponent(
    `Hi! Here's my feedback on Pebble:\n\n\n\n— diagnostics (please keep) —\n${diagnostics}\n`,
  );
  const href = `mailto:hello@pebble.app?subject=${subject}&body=${body}`;

  return (
    <div className="sticky top-0 z-40 px-3 pt-3">
      <AnimatePresence mode="wait" initial={false}>
        {!showLaunchPrompt ? (
          <motion.div
            key="banner"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.18 }}
            className="glass mx-auto flex max-w-md items-center gap-2 rounded-full px-3.5 py-2 text-xs"
            style={{ background: 'hsl(var(--primary) / 0.10)' }}
          >
            <Sparkles className="h-3.5 w-3.5 shrink-0 text-primary" />
            <span className="min-w-0 flex-1 truncate">
              Pebble Beta · <span className="font-mono">v{APP_VERSION}</span>.{' '}
              <a
                href={href}
                onClick={() => track('beta_feedback_clicked')}
                className="font-medium text-primary hover:underline"
              >
                Send feedback →
              </a>
            </span>
            <button
              onClick={onDismissClick}
              className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-foreground/10 transition"
              aria-label="Dismiss"
            >
              <X className="h-3 w-3" />
            </button>
          </motion.div>
        ) : (
          <motion.div
            key="launch"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="glass mx-auto flex max-w-md items-center gap-3 rounded-2xl px-3.5 py-2.5 text-xs"
            style={{ background: 'hsl(var(--primary) / 0.10)' }}
          >
            <Bell className="h-4 w-4 shrink-0 text-primary" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-foreground">
                {done ? "You're on the list 🌸" : 'Email me when Pebble launches?'}
              </div>
              {!done && (
                <div className="text-muted-foreground truncate">
                  We'll use {email || 'your account email'}.
                </div>
              )}
            </div>
            {!done ? (
              <>
                <button
                  onClick={onOptIn}
                  disabled={busy}
                  className="shrink-0 rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-medium hover:opacity-90 transition disabled:opacity-50 inline-flex items-center gap-1"
                >
                  {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Yes please'}
                </button>
                <button
                  onClick={finallyDismiss}
                  className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-foreground/10 transition"
                  aria-label="No thanks"
                >
                  <X className="h-3 w-3" />
                </button>
              </>
            ) : (
              <Check className="h-4 w-4 text-primary" />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
